import React, { useEffect, useMemo, useRef, useState } from "react";
import FrontendConfig from "../assets/config.js";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

function IncidentView({ toggleShowModal, toggleShowSuccessModal }) {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [loading, setLoading] = useState(true);
  const [incidentDetails, setIncidentDetails] = useState(null);

  const [reason, setReason] = useState("UNAUTHORIZED_ACCESS_ATTEMPT");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [submitted, setSubmitted] = useState(false);

  const hasLoadedRef = useRef(false);

  // Read query params from /incident?id=...&token=...
  const query = useMemo(() => new URLSearchParams(window.location.hash.split("?")[1] || ""), []);
  const incidentId = query.get("id") || "";
  const incidentToken = query.get("token") || "";

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const controller = new AbortController();

    async function loadIncident() {
      if (!incidentId || !incidentToken) {
        setLoading(false);
        toggleShowModal(
          <>
            <div>
              <i className="fas fa-3x fa-exclamation-triangle"></i>
            </div>
            <p>Incident link is invalid.</p>
          </>
        );
        return;
      }

      try {
        const response = await fetch(
          `${FrontendConfig.api_endpoint}/incident?id=${encodeURIComponent(incidentId)}&token=${encodeURIComponent(incidentToken)}`,
          { signal: controller.signal }
        );

        const responseData = await response.json();

        if (response.ok && responseData.status === "Success") {
          setIncidentDetails(responseData.details);
        } else {
          toggleShowModal(
            <>
              <div>
                <i className="fas fa-3x fa-exclamation-triangle"></i>
              </div>
              <p>{responseData.message || "Unable to load the incident."}</p>
            </>
          );
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          toggleShowModal(
            <>
              <div>
                <i className="fas fa-3x fa-exclamation-triangle"></i>
              </div>
              <p>Unable to load the incident.</p>
            </>
          );
        }
      } finally {
        setLoading(false);
      }
    }

    loadIncident();

    return () => controller.abort();
  }, [incidentId, incidentToken]);

  async function submitIncidentReport() {
    if (submitting) return;

    if (!comment.trim()) {
      toggleShowModal(
        <>
          <div>
            <i className="fas fa-3x fa-exclamation-triangle"></i>
          </div>
          <p>Please enter a comment before submitting the report.</p>
        </>
      );
      return;
    }

    try {
      setSubmitting(true);

      // Initializing captchaToken
      let captchaToken = null;

      if (executeRecaptcha) {
        try {
          captchaToken = await executeRecaptcha("reportincident");
        } catch (err) {
          console.log("recaptcha error:", err);
        }
      }

      const response = await fetch(FrontendConfig.api_endpoint + "/incident", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Captcha-Token": captchaToken
        },
        body: JSON.stringify({
          mode: "EXISTING_INCIDENT",
          reason: reason,
          comment: comment,
          incident: {
            incidentId: incidentId,
            incidentToken: incidentToken
          }
        })
      });

      const responseData = await response.json();

      if (response.ok && responseData.status === "Success") {
        setSubmitted(true);
        setComment("");
        setReason("UNAUTHORIZED_ACCESS_ATTEMPT");
        return;
      }

      toggleShowModal(
        <>
          <div>
            <i className="fas fa-3x fa-exclamation-triangle"></i>
          </div>
          <p>{responseData.message || "Unable to submit the report."}</p>
        </>
      );
    } catch (error) {
      toggleShowModal(
        <>
          <div>
            <i className="fas fa-3x fa-exclamation-triangle"></i>
          </div>
          <p>Unable to submit the report.</p>
        </>
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="content">
      <div className="row">
        <div className="col-lg-12 col-md-10 col-sm-11 ml-auto mr-auto">
          <div
            className="card shadow-sm border-0"
            style={{ borderRadius: "18px", overflow: "hidden" }}
          >
            {!submitted && (
              <div className="card-header pb-0" style={{ borderBottom: "none" }}>
                <h4 className="title text-center mb-2">Report verification email</h4>
                <p className="text-center text-muted mb-10">
                  Help us understand why you are reporting this verification email.
                </p>
              </div>
            )}

            <div className="card-body px-md-5 py-4">
              {loading && <p className="text-center">Loading...</p>}

              {!loading && submitted && ( 
                <div className="text-center py-4">
                  <div className="mb-3">
                    <i className="fas fa-3x fa-check-circle text-success"></i>
                  </div>
                  <h4 className="mb-2">Report submitted</h4>
                  <p className="text-muted mb-2">
                    Your report was submitted successfully.
                  </p>
                  <p className="text-muted mb-0">
                    You can close this tab now.
                  </p>
                </div>
              )}

              {!loading && !submitted && incidentDetails && (
                <>
                  <details className="mb-4">
                    <summary
                      style={{
                        cursor: "pointer",
                        fontSize: "14px",
                        color: "#6c757d"
                      }}
                    >
                      Incident details
                    </summary>

                    <div className="mt-2 text-left" style={{ fontSize: "13px", color: "#6c757d" }}>
                      <div className="mb-2">
                        <strong>Secret ID:</strong><br />
                        <code style={{ fontSize: "12px", wordBreak: "break-all" }}>
                          {incidentDetails.secretId}
                        </code>
                      </div>

                      <div>
                        <strong>Recipient email:</strong><br />
                        <span style={{ wordBreak: "break-word" }}>
                          {incidentDetails.recipientEmail}
                        </span>
                      </div>
                    </div>
                  </details>

                  <div className="form-group mb-4">
                    <label htmlFor="reason" className="font-weight-bold">
                      Reason
                    </label>
                    <select
                      id="reason"
                      className="form-control"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      disabled={submitted || submitting}
                      style={{ borderRadius: "12px" }}
                    >
                      <option value="UNAUTHORIZED_ACCESS_ATTEMPT">
                        Unauthorized access attempt
                      </option>
                      <option value="UNRELATED_EMAIL">
                        Email not meant for me
                      </option>
                    </select>
                  </div>


                  <div className="form-group mb-4">
                    <label htmlFor="comment" className="font-weight-bold">
                      Comment
                    </label>
                    <textarea
                      id="comment"
                      className="form-control"
                      disabled={submitted || submitting}
                      rows="3"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Tell us what happened."
                      maxLength="500"
                      style={{ resize: "vertical", borderRadius: "12px" }}
                    />
                    <small className="form-text text-muted">
                      Add any details that may help us review this report.
                    </small>
                    <div className="text-right text-muted mt-2">
                      {comment.length}/500
                    </div>
                  </div>

                  <div className="text-center mt-4">
                    <button
                      className="btn btn-danger btn-round px-5"
                      type="button"
                      onClick={submitIncidentReport}
                      disabled={submitted || submitting}
                    >
                      {submitting ? "Submitting..." : "Submit report"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IncidentView;